// frontend/src/components/asset/AssetAssigneeCombobox.js
import React, { useEffect, useMemo, useState } from 'react';
import SearchableCombobox from '../UI/SearchableCombobox';
import ClassificationBadge from '../UI/ClassificationBadge';
import { useStaffList } from '../../hooks/useStaffData';

const isInactiveStatus = (status) => (status || '').toLowerCase() === 'inactive';

const formatAssigneeName = (emp) => {
  if (!emp) return '';
  const legal = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
  if (emp.alias?.trim()) return `${emp.alias.trim()} (${legal})`;
  return legal || emp.staff_id || '';
};

const departmentName = (emp) =>
  emp?.department?.department_name || emp?.department?.name || '';

/**
 * Asset assignment recipient picker.
 *
 * Backend `include_external` already hides inactive / offboarded people.
 * Client filter is defence-in-depth so a stale React Query page cannot
 * put a leaver back in the list.
 */
const AssetAssigneeCombobox = ({
  value,
  onChange,
  theme = 'admin',
  disabled = false,
  excludeStaffId,
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const shouldSearch = search.trim().length >= 2;

  const { data: staffListData, isLoading } = useStaffList(
    {
      search: shouldSearch ? search.trim() : undefined,
      page_size: 10,
      include_external: 'true',
    },
    { enabled: shouldSearch }
  );

  const items = useMemo(() => {
    if (!shouldSearch) return [];
    return (staffListData?.results || []).filter((emp) => {
      if (!emp?.staff_id) return false;
      if (excludeStaffId && emp.staff_id === excludeStaffId) return false;
      return !isInactiveStatus(emp.employment_status);
    });
  }, [staffListData, shouldSearch, excludeStaffId]);

  useEffect(() => {
    if (!value) {
      setSelected(null);
      return;
    }
    const fromItems = items.find((emp) => emp.staff_id === value);
    if (fromItems) setSelected(fromItems);
  }, [value, items]);

  const handleChange = (staffId) => {
    const next = items.find((emp) => emp.staff_id === staffId) || null;
    setSelected(next);
    onChange(staffId);
  };

  const emptyLabel = shouldSearch
    ? 'No assignable people found'
    : 'Type at least 2 characters to search';

  return (
    <SearchableCombobox
      items={items}
      value={value}
      onChange={handleChange}
      onSearch={setSearch}
      loading={shouldSearch && isLoading}
      loadingLabel="Searching..."
      displayFn={formatAssigneeName}
      displayValue={selected ? `${formatAssigneeName(selected)} (${selected.staff_id})` : ''}
      valueFn={(emp) => emp.staff_id}
      placeholder="Search employee or holder..."
      searchPlaceholder="Type name or staff ID..."
      emptyLabel={emptyLabel}
      variant="form"
      theme={theme}
      disabled={disabled}
      className="w-full"
      renderItem={({ item, itemValue, isSelected, isHighlighted, onMouseDownSelect }) => {
        const isOutsourced = item.employment_classification === 'outsourced';
        const highlight = theme === 'admin' ? 'bg-red-50' : 'bg-blue-50';
        return (
          <button
            key={itemValue}
            type="button"
            onMouseDown={onMouseDownSelect}
            className={`w-full text-left px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset ${
              theme === 'admin'
                ? 'focus-visible:ring-red-500 hover:bg-red-50'
                : 'focus-visible:ring-blue-500 hover:bg-blue-50'
            } ${isSelected || isHighlighted ? highlight : ''}`}
          >
            <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5 min-w-0">
              <span className="truncate">{formatAssigneeName(item)}</span>
              {isOutsourced && (
                <ClassificationBadge classification="outsourced" />
              )}
            </p>
            <p className="text-xs text-gray-500">
              {item.staff_id}
              {isOutsourced
                ? ' • Confirms receipt via emailed link'
                : departmentName(item)
                  ? ` • ${departmentName(item)}`
                  : ''}
            </p>
          </button>
        );
      }}
    />
  );
};

export default AssetAssigneeCombobox;
